export const PROJECT_ACTIONS = {
  LOAD_PROJECT: 'LOAD_PROJECT',
  UPDATE_PROJECT: 'UPDATE_PROJECT',
  UPDATE_SCENE: 'UPDATE_SCENE',
  UPDATE_SHOT: 'UPDATE_SHOT',
  RESIZE_SCENE: 'RESIZE_SCENE',
  DELETE_SCENE: 'DELETE_SCENE',
  REORDER_SCENES: 'REORDER_SCENES',
  SPLIT_SCENE: 'SPLIT_SCENE',
  SET_TRANSITION: 'SET_TRANSITION',
  SET_PLAYBACK_RATE: 'SET_PLAYBACK_RATE',
  ADD_SUBTITLE: 'ADD_SUBTITLE',
  UPDATE_SUBTITLE: 'UPDATE_SUBTITLE',
  UPDATE_ALL_SUBTITLES_STYLE: 'UPDATE_ALL_SUBTITLES_STYLE',
  DELETE_SUBTITLE: 'DELETE_SUBTITLE',
  // Text Overlays
  ADD_TEXT_OVERLAY: 'ADD_TEXT_OVERLAY',
  UPDATE_TEXT_OVERLAY: 'UPDATE_TEXT_OVERLAY',
  DELETE_TEXT_OVERLAY: 'DELETE_TEXT_OVERLAY',
  // Audio
  TOGGLE_MUTE_AUDIO: 'TOGGLE_MUTE_AUDIO',
  DELETE_AUDIO: 'DELETE_AUDIO',
};

export const initialProjectState = {
  id: null,
  name: 'Untitled',
  width: 800,
  height: 450,
  fps: 30,
  duration: 0,
  bgm: null,
  scenes: [],
  subtitles: [],
  //   {
//   "id": "sub-1714400000000-a1b2",
//   "text": "Hello world!",
//   "startTime": 5.0,
//   "endTime": 8.0,
//   "style": {
//     "fontFamily": "Inter, sans-serif",
//     "fontSize": 24,
//     "color": "#ffffff",
//     "background": "rgba(0,0,0,0.7)",
//     "borderRadius": 6,
//     "padding": "8px 20px",
//     "textShadow": "none",
//     "letterSpacing": 0,
//     "textTransform": "none"
//   }
// }
};

export function projectReducer(state, action) {
  switch (action.type) {
    case PROJECT_ACTIONS.LOAD_PROJECT: {
      const rawScenes = action.payload.scenes || [];
      
      // Filter out scenes without video and adjust audio timing
      const validScenes = [];
      let removedDuration = 0;
      
      for (const scene of rawScenes) {
        const hasVideo = scene.video && scene.video.src;
        const hasImages = scene.images && scene.images.length > 0;

        if (!hasVideo && !hasImages) {
          // This scene is being removed — accumulate its duration for shifting
          removedDuration += scene.duration || 0;
          continue;
        }
        
        let adjustedScene = { ...scene };

        // Clamp scene duration to video duration (only for video-based scenes)
        if (hasVideo) {
          const maxDur = scene.video?.videoDuration;
          if (maxDur && scene.duration > maxDur) {
            const clampedAmount = scene.duration - maxDur;
            removedDuration += clampedAmount;
            adjustedScene.duration = maxDur;
          }
        }
        
        // Shift audio playStart/playEnd by the total removed duration so far
        if (removedDuration > 0 && adjustedScene.audios && adjustedScene.audios.length > 0) {
          adjustedScene.audios = adjustedScene.audios.map(audio => ({
            ...audio,
            playStart: audio.playStart - removedDuration,
            playEnd: audio.playEnd - removedDuration,
          }));
        }
        
        validScenes.push(adjustedScene);
      }

      return {
        ...action.payload,
        scenes: validScenes,
        subtitles: action.payload.subtitles || [],
        textOverlays: action.payload.textOverlays || [],
        mutedAudios: {},
      };
    }

    case PROJECT_ACTIONS.UPDATE_PROJECT:
      return { ...state, ...action.payload };

    case PROJECT_ACTIONS.UPDATE_SCENE:
      return {
        ...state,
        scenes: state.scenes.map((scene, i) =>
          (scene.id === action.payload.id || i === action.payload.index)
            ? { ...scene, ...action.payload.updates }
            : scene
        ),
      };

    case PROJECT_ACTIONS.UPDATE_SHOT:
      // Legacy action — now updates scene-level video/audios/texts directly
      return {
        ...state,
        scenes: state.scenes.map((scene) =>
          scene.id === action.payload.sceneId
            ? { ...scene, ...action.payload.updates }
            : scene
        ),
      };

    case PROJECT_ACTIONS.ADD_SUBTITLE:
      return { ...state, subtitles: [...(state.subtitles || []), action.payload] };

    case PROJECT_ACTIONS.UPDATE_SUBTITLE:
      return {
        ...state,
        subtitles: (state.subtitles || []).map(s =>
          s.id === action.payload.id ? { ...s, ...action.payload.updates } : s
        ),
      };

    case PROJECT_ACTIONS.UPDATE_ALL_SUBTITLES_STYLE:
      return {
        ...state,
        subtitles: (state.subtitles || []).map(s => ({
          ...s,
          style: { ...(s.style || {}), ...action.payload }
        })),
      };

    case PROJECT_ACTIONS.DELETE_SUBTITLE:
      return {
        ...state,
        subtitles: (state.subtitles || []).filter(s => s.id !== action.payload.id),
      };

    case PROJECT_ACTIONS.ADD_TEXT_OVERLAY:
      return { ...state, textOverlays: [...(state.textOverlays || []), action.payload] };

    case PROJECT_ACTIONS.UPDATE_TEXT_OVERLAY:
      return {
        ...state,
        textOverlays: (state.textOverlays || []).map(o =>
          o.id === action.payload.id ? { ...o, ...action.payload.updates } : o
        ),
      };

    case PROJECT_ACTIONS.DELETE_TEXT_OVERLAY:
      return {
        ...state,
        textOverlays: (state.textOverlays || []).filter(o => o.id !== action.payload.id),
      };

    case PROJECT_ACTIONS.TOGGLE_MUTE_AUDIO: {
      const mutedAudios = { ...(state.mutedAudios || {}) };
      mutedAudios[action.payload.id] = !mutedAudios[action.payload.id];
      return { ...state, mutedAudios };
    }

    case PROJECT_ACTIONS.DELETE_AUDIO: {
      const { sceneId, audioId } = action.payload;
      return {
        ...state,
        scenes: state.scenes.map(scene => {
          if (scene.id === sceneId) {
            const filteredAudios = (scene.audios || []).filter(a => a.id !== audioId);
            return { ...scene, audios: filteredAudios };
          }
          return scene;
        }),
      };
    }

    case PROJECT_ACTIONS.DELETE_SCENE: {
      const { sceneId } = action.payload;
      const scenes = state.scenes;
      const sceneIndex = scenes.findIndex(s => s.id === sceneId);
      if (sceneIndex === -1) return state;

      const deletedScene = scenes[sceneIndex];
      const deletedDuration = deletedScene.duration || 0;

      // Calculate the absolute start/end time of the deleted scene
      let sceneStart = 0;
      for (let i = 0; i < sceneIndex; i++) {
        sceneStart += scenes[i].duration || 0;
      }
      const sceneEnd = sceneStart + deletedDuration;

      // Remove the scene
      const newScenes = scenes.filter(s => s.id !== sceneId);

      // Shift audios in subsequent scenes
      const adjustedScenes = newScenes.map((scene, i) => {
        if (i >= sceneIndex) {
          const shiftedAudios = (scene.audios || []).map(audio => ({
            ...audio,
            playStart: audio.playStart - deletedDuration,
            playEnd: audio.playEnd - deletedDuration,
          }));
          return { ...scene, audios: shiftedAudios };
        }
        return scene;
      });

      // Remove subtitles within the deleted scene's range, shift those after
      const newSubtitles = (state.subtitles || [])
        .filter(sub => !(sub.startTime >= sceneStart && sub.endTime <= sceneEnd))
        .map(sub => {
          if (sub.startTime >= sceneEnd) {
            return {
              ...sub,
              startTime: sub.startTime - deletedDuration,
              endTime: sub.endTime - deletedDuration,
              words: (sub.words || []).map(w => ({
                ...w,
                startTime: w.startTime - deletedDuration,
                endTime: w.endTime - deletedDuration,
              })),
            };
          }
          return sub;
        });

      // Remove text overlays within the deleted scene's range, shift those after
      const newTextOverlays = (state.textOverlays || [])
        .filter(o => !(o.startTime >= sceneStart && o.endTime <= sceneEnd))
        .map(o => {
          if (o.startTime >= sceneEnd) {
            return {
              ...o,
              startTime: o.startTime - deletedDuration,
              endTime: o.endTime - deletedDuration,
            };
          }
          return o;
        });

      return { ...state, scenes: adjustedScenes, subtitles: newSubtitles, textOverlays: newTextOverlays };
    }

    case PROJECT_ACTIONS.REORDER_SCENES: {
      const { sceneId, targetIndex } = action.payload;
      const scenes = [...state.scenes];
      const fromIndex = scenes.findIndex(s => s.id === sceneId);
      if (fromIndex === -1) return state;
      const [moved] = scenes.splice(fromIndex, 1);
      scenes.splice(targetIndex, 0, moved);
      return { ...state, scenes };
    }

    case PROJECT_ACTIONS.SPLIT_SCENE: {
      const { sceneId, splitAt } = action.payload;
      const idx = state.scenes.findIndex(s => s.id === sceneId);
      if (idx === -1) return state;
      const scene = state.scenes[idx];
      if (splitAt <= 0 || splitAt >= scene.duration) return state;

      const newId = `scene-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const firstPart = { ...scene, duration: splitAt };
      const secondPart = {
        ...scene,
        id: newId,
        duration: scene.duration - splitAt,
        video: scene.video ? {
          ...scene.video,
          id: `vid-${Date.now()}`,
          videoStart: (scene.video.videoStart || 0) + splitAt,
        } : null,
        audios: [],
        texts: scene.texts ? scene.texts.map(t => ({ ...t, id: `txt-${Date.now()}-${Math.random().toString(36).slice(2, 4)}` })) : [],
      };

      const newScenes = [...state.scenes];
      newScenes.splice(idx, 1, firstPart, secondPart);
      return { ...state, scenes: newScenes };
    }

    case PROJECT_ACTIONS.SET_TRANSITION: {
      const { sceneId, effect, duration } = action.payload;
      return {
        ...state,
        scenes: state.scenes.map(s =>
          s.id === sceneId
            ? { ...s, transition: { effect, duration: duration || 0.5 } }
            : s
        ),
      };
    }

    case PROJECT_ACTIONS.SET_PLAYBACK_RATE: {
      const { sceneId, rate } = action.payload;
      return {
        ...state,
        scenes: state.scenes.map(s =>
          s.id === sceneId && s.video
            ? { ...s, video: { ...s.video, playbackRate: rate } }
            : s
        ),
      };
    }

    case PROJECT_ACTIONS.RESIZE_SCENE: {
      // Resizes a scene's duration from the timeline track bar.
      // Shifts all audio/subtitle/textOverlay playStart/playEnd in subsequent scenes by the delta.
      const { sceneIndex, newDuration } = action.payload;
      const oldDuration = state.scenes[sceneIndex]?.duration || 0;
      const delta = oldDuration - newDuration; // positive = scene got shorter, negative = longer

      if (delta === 0) return state;

      const newScenes = state.scenes.map((scene, i) => {
        if (i === sceneIndex) {
          return { ...scene, duration: newDuration };
        }
        if (i > sceneIndex) {
          // Shift audio playStart/playEnd for subsequent scenes
          const shiftedAudios = (scene.audios || []).map(audio => ({
            ...audio,
            playStart: audio.playStart - delta,
            playEnd: audio.playEnd - delta,
          }));
          return { ...scene, audios: shiftedAudios };
        }
        return scene;
      });

      // Calculate the absolute end time of the resized scene (before resize)
      let cumulativeBeforeResized = 0;
      for (let i = 0; i <= sceneIndex; i++) {
        cumulativeBeforeResized += state.scenes[i]?.duration || 0;
      }
      const shiftThreshold = cumulativeBeforeResized;

      // Shift subtitles that start after the resized scene
      const newSubtitles = (state.subtitles || []).map(sub => {
        if (sub.startTime >= shiftThreshold) {
          return {
            ...sub,
            startTime: sub.startTime - delta,
            endTime: sub.endTime - delta,
            words: (sub.words || []).map(w => ({
              ...w,
              startTime: w.startTime - delta,
              endTime: w.endTime - delta,
            })),
          };
        }
        return sub;
      });

      // Shift text overlays that start after the resized scene
      const newTextOverlays = (state.textOverlays || []).map(overlay => {
        if (overlay.startTime >= shiftThreshold) {
          return {
            ...overlay,
            startTime: overlay.startTime - delta,
            endTime: overlay.endTime - delta,
          };
        }
        return overlay;
      });

      return { ...state, scenes: newScenes, subtitles: newSubtitles, textOverlays: newTextOverlays };
    }

    default:
      return state;
  }
}
